"use client";

import { Component, type ReactNode, useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { Button } from "@/ui/components/Button";
import { Skeleton } from "@/ui/components/Skeleton";
import { useCandidateDossier } from "@/lib/signals/useCandidateDossier";
import type { CandidateDossier } from "@/lib/signals/types";
import { formatDate } from "@/lib/signals/presentation";
import styles from "./SignalsView.module.css";

const TIER_LABEL = { read: "Read", stored: "Stored", found: "Found in search", blocked: "Could not open" } as const;

/** Shows a fallback instead of blanking the page when a dossier can't be rendered. */
class DossierBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  render() {
    return this.state.failed ? <p className={styles.muted} role="alert">The dossier couldn’t be shown.</p> : this.props.children;
  }
}

export function DossierView({ dossier: { dossier: raw, finishedAt } }: { dossier: CandidateDossier }) {
  const [showSources, setShowSources] = useState(false);
  const [target, setTarget] = useState<number | null>(null);
  // The source list renders on the next commit, so scroll once it (and the target) exists.
  useEffect(() => {
    if (target != null) document.getElementById(`dossier-source-${target}`)?.scrollIntoView({ block: "center" });
  }, [target, showSources]);
  const sourceLink = (n: number) => <a key={n} className={styles.cite} href={`#dossier-source-${n}`}
    onClick={(event) => { event.preventDefault(); setShowSources(true); setTarget(n); }}>[{n}]</a>;
  // The payload comes from DOTS: default anything missing rather than trusting its shape.
  const dossier = {
    ...raw,
    identity: { confirmed: false, name: null, profiles: [], ...raw?.identity },
    sections: (raw?.sections ?? []).map((section) => ({ ...section, items: section.items ?? [] })),
    sources: raw?.sources ?? [],
  };
  const coverage = { searches: 0, searchesWithResults: 0, resultsFound: 0, pagesRead: 0, pagesStored: 0, blocked: 0, ...raw?.coverage, emptyLanes: raw?.coverage?.emptyLanes ?? [] };
  const sections = dossier.sections.filter((section) => section.id !== "identity");
  const identityItems = dossier.sections.find((section) => section.id === "identity")?.items ?? [];
  return <section className={styles.dossier} aria-labelledby="dossier-heading">
    <header className={styles.dossierHeader}>
      <h3 id="dossier-heading">Public dossier</h3>
      <p className={styles.muted}>Researched {formatDate(finishedAt ?? dossier.generatedAt ?? null)}{coverage.searches > 0 && ` · ${coverage.searches} searches`} · {coverage.pagesRead} pages read</p>
    </header>
    <div className={styles.dossierSection}>
      <h4>Identity and profiles</h4>
      <p>{dossier.identity.confirmed ? `Confirmed as ${dossier.identity.name}.` : "Identity could not be confirmed from public sources."}</p>
      {(dossier.identity.profiles.length > 0 || identityItems.length > 0) && <ul>
        {dossier.identity.profiles.map((profile) => <li key={profile.url}><a className={styles.sourceLink} href={profile.url} target="_blank" rel="noopener noreferrer">
          {profile.title || profile.url.replace(/^https?:\/\/(www\.)?/, "")}<ExternalLink size={12} aria-hidden /></a></li>)}
        {identityItems.map((item, index) => <li key={index}>{item.text} {(item.sources ?? []).map(sourceLink)}</li>)}
      </ul>}
    </div>
    {sections.map((section) => <div key={section.id} className={styles.dossierSection}>
      <h4>{section.title}</h4>
      <ul>{section.items.map((item, index) => <li key={index}>
        {item.date && <time className={styles.muted} dateTime={item.date}>{formatDate(item.date)} · </time>}
        {item.text}{item.status === "conflicting" && <strong> (conflicts with the CV)</strong>} {(item.sources ?? []).map(sourceLink)}
      </li>)}</ul>
    </div>)}
    {sections.length === 0 && <p className={styles.muted}>Research found no public information beyond the CV.</p>}
    <p className={styles.muted}>
      {coverage.searches > 0 && `${coverage.searchesWithResults} of ${coverage.searches} searches returned results · `}{coverage.pagesStored} more pages stored · {coverage.blocked} could not be opened
      {coverage.emptyLanes.length > 0 && ` · nothing found for ${coverage.emptyLanes.map((lane) => lane.replace(/_/g, " ")).join(", ")}`}
    </p>
    <Button variant="tertiary" size="sm" aria-expanded={showSources} onClick={() => setShowSources(!showSources)}>
      {showSources ? "Hide sources" : `All ${dossier.sources.length} sources`}</Button>
    {showSources && <ol className={styles.sourceIndex}>{dossier.sources.map((source) => <li key={source.n} id={`dossier-source-${source.n}`} value={source.n}>
      <a className={styles.sourceLink} href={source.url} target="_blank" rel="noopener noreferrer">{source.title || source.url}<ExternalLink size={12} aria-hidden /></a>
      <span className={styles.muted}> · {TIER_LABEL[source.tier]}{source.identity === "confirmed" ? " · about this person" : ""}</span>
    </li>)}</ol>}
  </section>;
}

export function CandidateDossier({ candidateId }: { candidateId: string }) {
  const query = useCandidateDossier(candidateId);
  if (query.isPending) return <div className={styles.dossier} role="status" aria-label="Loading dossier"><Skeleton variant="rounded" height={160} /></div>;
  if (query.isError) return <p className={styles.muted} role="alert">The dossier couldn’t be loaded. <Button variant="tertiary" size="sm" onClick={() => void query.refetch()}>Try again</Button></p>;
  if (!query.data) return <p className={styles.muted}>No dossier yet. It is written when research finishes.</p>;
  return <DossierBoundary><DossierView dossier={query.data} /></DossierBoundary>;
}
